<template>
  <HeadTitle
    title="Configuraciones de cartas"
    description="Lista de las configuraciones de cartas y certificaciones creadas en el sistema."
  />
  <Divider />
  <div class="flex flex-row">
    <h2 class="font-normal">Cartas internas</h2>
    <InfoButton
      message="Aquí se listan las configuraciones de cartas internas que se han creado en el sistema."
    />
  </div>
  <Toolset
    ref="toolset"
    :searcher="handleInternalSearch"
    add-button-icon="pi pi-plus"
    add-button-label="Crear nueva carta"
    :to-add="toAdd"
    class="mt-5"
  >
    <Button
      class="text-sm font-light mr-3"
      label="Reporte de cartas generadas"
      severity="secondary"
      icon=""
      @click="router.push('/reports/letters')"
    />
  </Toolset>
  <DataTable
    class="tabla mb-5 mt-3"
    :value="letterConfigs.data"
    paginator
    lazy
    :loading="internalLoading"
    :rows="internalPagination.perPage"
    :first="(internalPagination.page - 1) * internalPagination.perPage"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    tableStyle="min-width: 50rem"
    removableSort
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :totalRecords="letterConfigs.meta.total"
    :currentPageReportTemplate="currentPageReportTemplate"
    scrollable
    scrollHeight="600px"
    sortField="updatedAt"
    :sortOrder="-1"
    @page="handleInternalPageChange"
  >
    <template #empty> No se encontraron cartas.</template>
    <Column field="name" header="Nombre" sortable></Column>
    <Column field="description" header="Descripción" sortable></Column>
    <Column field="generationType" header="Tipo de carta" sortable> </Column>
    <Column field="payrolls" header="Nóminas" sortable>
      <template #body="{ data, field }">
        <span class="font-semibold text-overflow-elipsis">{{
          payrollsToString(data.payrolls)
        }}</span>
      </template>
    </Column>
    <Column field="updatedAt" header="Última actualización" sortable>
      <template #body="{ data, field }">
        <span>{{ formatDateWithHour(data[field]) }}</span>
      </template>
    </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <ConfirmPopup></ConfirmPopup>
        <div class="actions">
          <Button
            v-tooltip.bottom="'Ver detalles'"
            class="action-btn view p-button-sm mr-2"
            icon="pi pi-eye"
            @click="goToDetails(data['id'], data['generationType'])"
          />
          <Button
            v-tooltip.bottom="'Eliminar'"
            class="action-btn view p-button-sm px-4 bg-red-500 border-red-500"
            :loading="internalDeletingId === data['id']"
            :disabled="internalDeletingId === data['id']"
            @click="deleteLetterConfig($event, data['id'], false)"
          >
            <i class="pi pi-trash text-white"></i>
          </Button>
        </div>
      </template>
    </Column>
  </DataTable>
  <Divider />
  <div class="flex flex-row">
    <h2 class="font-normal">Cartas externas</h2>
    <InfoButton
      message="Aquí se listan las configuraciones de cartas externas precargadas en el sistema."
    />
  </div>
  <ExternalLettersSection
    :letter-configs="externalLetterConfigs"
    :loading="externalLoading"
    :deleting-id="externalDeletingId"
    :current-page-report-template="currentPageReportTemplateExt"
    :payrolls-to-string="payrollsToString"
    :format-date-with-hour="formatDateWithHour"
    @search="handleExternalSearch"
    @add="addExternalLetter"
    @view="({ id, generationType }) => goToDetails(id, generationType)"
    @delete="({ event, id }) => deleteLetterConfig(event, id, true)"
    @page="handleExternalPageChange"
  />
  <Footer>
    <template #left>
      <AuditButton
        :entities="['LetterCertifConfig']"
        :root-id="company?.id"
        only-deleted
        label="Ver registros eliminados"
        :disabled="!company?.id"
      />
    </template>
  </Footer>
</template>
<script lang="ts" setup>
  import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
  import Toolset from '@/shared/components/Toolset.vue'
  import Footer from '@/shared/components/Footer.vue'
  import LetterConfigList from '../models/LetterConfig'
  import {
    getLettersConfig,
    deleteLetterConfigById,
  } from '../services/LetterConfgServices'
  import useCompany from '@/store/company'
  import { storeToRefs } from 'pinia'
  import router from '@/router'
  import { formatDateWithHour } from '@/shared/utils/fomartDateWithHours'
  import Payroll from '@/modules/payrolls-config/models/Payroll'
  import { useConfirm } from 'primevue/useconfirm'
  import useNotifications from '@/store/notification'
  import Divider from 'primevue/divider'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import ExternalLettersSection from '../components/ExternalLettersSection.vue'
  import type PaginatedResult from '@/shared/dtos/PaginatedResult'
  import { DataTablePageEvent } from 'primevue/datatable'

  const companyStore = useCompany()
  const notificationStore = useNotifications()
  const confirm = useConfirm()

  const { company } = storeToRefs(companyStore)

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
    internalPagination.page = 1
    externalPagination.page = 1
    await fetchData()
  })

  const letterConfigs = reactive<PaginatedResult<LetterConfigList>>({
    data: [],
    meta: {
      total: 0,
      lastPage: 0,
      currentPage: 1,
      perPage: 10,
      prev: null,
      next: null,
    },
  })

  const externalLetterConfigs = reactive<PaginatedResult<LetterConfigList>>({
    data: [],
    meta: {
      total: 0,
      lastPage: 0,
      currentPage: 1,
      perPage: 10,
      prev: null,
      next: null,
    },
  })

  const internalPagination = reactive({ page: 1, perPage: 10 })
  const externalPagination = reactive({ page: 1, perPage: 10 })

  const internalLoading = ref(false)
  const externalLoading = ref(false)
  const internalDeletingId = ref<string | null>(null)
  const externalDeletingId = ref<string | null>(null)

  const toolset = ref<{ search: any } | null>(null)
  const externalSearch = ref('')

  onMounted(async () => {
    await fetchData()
  })

  onUnmounted(() => {
    companyStore.$subscribe(() => {})
  })

  const toAdd = () => {
    router.push('/letterConfig/add')
  }

  const addExternalLetter = () => {
    router.push('/letterExternals/create')
  }

  const handleExternalSearch = async (searchTerm: string) => {
    externalSearch.value = searchTerm
    externalPagination.page = 1
    await fetchExternalLetters()
  }

  const extractInternalSearch = () => {
    if (!toolset.value) return ''
    const { search } = toolset.value
    if (typeof search === 'string') return search
    if (search && typeof search.value === 'string') return search.value
    return ''
  }

  const handleInternalSearch = async () => {
    internalPagination.page = 1
    await fetchInternalLetters()
  }

  const goToDetails = (id: string, generationType: string) => {
    if (generationType === 'Externa')
      router.push(`/letterExternals/detail/${id}`)
    else router.push(`/letterConfig/detail/${id}`)
  }

  const deleteLetterConfig = (event: any, id: string, isExternal: boolean) => {
    confirm.require({
      target: event.currentTarget,
      message:
        '¿Estás seguro de que deseas eliminar esta configuración de carta?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        const setDeleting = (value: string | null) =>
          isExternal
            ? (externalDeletingId.value = value)
            : (internalDeletingId.value = value)

        setDeleting(id)
        try {
          const response = await deleteLetterConfigById(id)
          if (response === 'OK') {
            await notificationStore.addSuccesNotification({
              detail:
                'La configuración de carta ha sido eliminada correctamente.',
              severity: 'success',
              summary: 'Eliminación exitosa',
            })

            if (isExternal) {
              await fetchExternalLetters()
            } else {
              await fetchInternalLetters()
            }
          }
        } finally {
          setDeleting(null)
        }
      },
    })
  }

  const fetchData = async () => {
    await Promise.all([fetchInternalLetters(), fetchExternalLetters()])
  }

  const fetchInternalLetters = async () => {
    internalLoading.value = true
    try {
      const response = await getLettersConfig({
        companyId: company.value?.id,
        pagination: {
          search: extractInternalSearch(),
          page: internalPagination.page,
          limit: internalPagination.perPage,
        },
        isExternal: false,
      })

      letterConfigs.data = response.data.map((letter) => ({
        ...letter,
        generationType: tranlateApprovalType(letter.generationType) || '',
      }))

      letterConfigs.meta = response.meta
      internalPagination.page =
        response.meta.currentPage || internalPagination.page
      internalPagination.perPage =
        response.meta.perPage || internalPagination.perPage
    } finally {
      internalLoading.value = false
    }
  }

  const fetchExternalLetters = async () => {
    externalLoading.value = true
    try {
      const response = await getLettersConfig({
        companyId: company.value?.id,
        pagination: {
          search: externalSearch.value,
          page: externalPagination.page,
          limit: externalPagination.perPage,
        },
        isExternal: true,
      })

      externalLetterConfigs.data = response.data.map((letter) => ({
        ...letter,
        generationType: tranlateApprovalType(letter.generationType) || '',
      }))

      externalLetterConfigs.meta = response.meta
      externalPagination.page =
        response.meta.currentPage || externalPagination.page
      externalPagination.perPage =
        response.meta.perPage || externalPagination.perPage
    } finally {
      externalLoading.value = false
    }
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${letterConfigs.meta.total}`
  })

  const currentPageReportTemplateExt = computed(() => {
    return `{first} a {last} de ${externalLetterConfigs.meta.total}`
  })

  const handleInternalPageChange = async ({
    page,
    rows,
  }: DataTablePageEvent) => {
    internalPagination.page = page + 1
    internalPagination.perPage = rows
    await fetchInternalLetters()
  }

  const handleExternalPageChange = async ({
    page,
    perPage,
  }: {
    page: number
    perPage: number
  }) => {
    externalPagination.page = page
    externalPagination.perPage = perPage
    await fetchExternalLetters()
  }

  const tranlateApprovalType = (approvalType: string) => {
    switch (approvalType) {
      case 'WITH_APPROVAL':
        return 'Requiere aprobación'
      case 'WITH_MULTIPLE_APPROVALS':
        return 'Multiples aprobadores'
      case 'AUTOMATIC':
        return 'Automática'
      case 'EXTERNAL':
        return 'Externa'
    }
  }

  const payrollsToString = (payrolls: Payroll[]) => {
    return payrolls.map((p) => p.code + '-' + p.name).join(', ')
  }
</script>
